import { supabase } from './supabase';

export type BrandGuidelines = {
  business_id: string;
  header_color: string;
  button_color: string;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  display_font: string;
  body_font: string;
  tagline: string;
  voice_tone: string;
  dos: string;
  donts: string;
  preferred_words: string;
  avoided_words: string;
  freeform_notes: string;
  website_url: string;
  online_booking_url: string;
  created_at?: string;
  updated_at?: string;
};

export type BrandGuidelinesInput = Omit<
  BrandGuidelines,
  'business_id' | 'created_at' | 'updated_at'
>;

export const EMPTY_BRAND_GUIDELINES: BrandGuidelinesInput = {
  header_color: '',
  button_color: '',
  primary_color: '',
  secondary_color: '',
  accent_color: '',
  display_font: '',
  body_font: '',
  tagline: '',
  voice_tone: '',
  dos: '',
  donts: '',
  preferred_words: '',
  avoided_words: '',
  freeform_notes: '',
  website_url: '',
  online_booking_url: '',
};

/** Normalize a URL for storage/display (adds https:// when missing). */
export function normalizeBrandUrl(raw: string | null | undefined): string {
  const v = (raw || '').trim();
  if (!v) return '';
  if (/^https?:\/\//i.test(v)) return v;
  return `https://${v}`;
}

function trimFields(input: BrandGuidelinesInput): BrandGuidelinesInput {
  return {
    header_color: (input.header_color || '').trim(),
    button_color: (input.button_color || '').trim(),
    primary_color: (input.primary_color || '').trim(),
    secondary_color: (input.secondary_color || '').trim(),
    accent_color: (input.accent_color || '').trim(),
    display_font: (input.display_font || '').trim(),
    body_font: (input.body_font || '').trim(),
    tagline: (input.tagline || '').trim(),
    voice_tone: (input.voice_tone || '').trim(),
    dos: (input.dos || '').trim(),
    donts: (input.donts || '').trim(),
    preferred_words: (input.preferred_words || '').trim(),
    avoided_words: (input.avoided_words || '').trim(),
    freeform_notes: (input.freeform_notes || '').trim(),
    website_url: normalizeBrandUrl(input.website_url),
    online_booking_url: normalizeBrandUrl(input.online_booking_url),
  };
}

export function hasAnyBrandGuidelines(
  guidelines: BrandGuidelinesInput | BrandGuidelines | null | undefined,
): boolean {
  if (!guidelines) return false;
  const t = trimFields(guidelines);
  return Object.values(t).some((v) => v.length > 0);
}

export type BrandSetupStatus = 'complete' | 'partial' | 'none';

export type BrandSetupSummary = {
  status: BrandSetupStatus;
  /** Fields with a value, logo included. */
  filled: number;
  /** Every field the Brand Guidelines page shows, logo included. */
  total: number;
};

/**
 * How far along the brand guidelines are: every field (logo included) → complete,
 * at least one → partial, nothing → none. Mirrors the fields on the Brand Guidelines page.
 */
export function brandSetupSummary(
  guidelines: BrandGuidelinesInput | BrandGuidelines | null | undefined,
  logoUrl: string | null | undefined,
): BrandSetupSummary {
  const t = guidelines ? trimFields(guidelines) : EMPTY_BRAND_GUIDELINES;
  const values = [...Object.values(t), (logoUrl || '').trim()];
  const filled = values.filter((v) => v.length > 0).length;
  const total = values.length;
  const status: BrandSetupStatus = filled === 0 ? 'none' : filled === total ? 'complete' : 'partial';
  return { status, filled, total };
}

export const BRAND_SETUP_LABEL: Record<BrandSetupStatus, string> = {
  complete: 'Set up',
  partial: 'Partially set up',
  none: 'Not set up',
};

/** Format filled brand fields for AI system/user prompts. Returns '' when empty. */
export function formatBrandGuidelinesForPrompt(
  guidelines: BrandGuidelinesInput | BrandGuidelines | null | undefined,
  opts?: {
    logoUrl?: string | null;
    /** Used when guidelines.website_url is empty (e.g. Nexus site). */
    websiteUrlFallback?: string | null;
    /** Used when guidelines.online_booking_url is empty. */
    onlineBookingUrlFallback?: string | null;
  },
): string {
  if (!guidelines && !opts?.logoUrl && !opts?.websiteUrlFallback && !opts?.onlineBookingUrlFallback) {
    return '';
  }
  const t = guidelines ? trimFields(guidelines) : EMPTY_BRAND_GUIDELINES;
  const lines: string[] = [];

  if (opts?.logoUrl?.trim()) {
    lines.push(`Logo URL (central business logo): ${opts.logoUrl.trim()}`);
  }
  if (t.tagline) lines.push(`Tagline / brand description: ${t.tagline}`);
  if (t.voice_tone) lines.push(`Voice & tone: ${t.voice_tone}`);

  const websiteUrl = t.website_url || normalizeBrandUrl(opts?.websiteUrlFallback);
  const bookingUrl = t.online_booking_url || normalizeBrandUrl(opts?.onlineBookingUrlFallback);
  if (websiteUrl) {
    lines.push(
      `Website URL: ${websiteUrl} — use this for “Visit website” / learn-more links and when a general site CTA is appropriate`,
    );
  }
  if (bookingUrl) {
    lines.push(
      `Online Booking URL: ${bookingUrl} — prefer this for Book Now / Schedule / primary booking CTAs and button links`,
    );
  }

  const colorLines: string[] = [];
  if (t.header_color) {
    colorLines.push(
      `Header color ${t.header_color} — always use this as the background for email/page headers and top banners when designing or describing layouts`,
    );
  }
  if (t.button_color) {
    colorLines.push(
      `Button color ${t.button_color} — always use this as the background for primary buttons and CTA buttons (choose contrasting button text color)`,
    );
  }
  if (t.primary_color) {
    colorLines.push(
      `Primary color ${t.primary_color} — main brand color for links, emphasis, and brand accents${!t.button_color ? ' (also use for buttons if no button color is set)' : ''}`,
    );
  }
  if (t.secondary_color) {
    colorLines.push(
      `Secondary color ${t.secondary_color} — supporting UI (secondary buttons, chips, borders)`,
    );
  }
  if (t.accent_color) {
    colorLines.push(
      `Accent color ${t.accent_color} — highlights, badges, and emphasis only`,
    );
  }
  if (colorLines.length > 0) {
    lines.push(`Brand colors (use only colors listed; leave roles blank if not set):`);
    colorLines.forEach((c) => lines.push(`  • ${c}`));
  }

  if (t.display_font || t.body_font) {
    if (t.display_font) {
      lines.push(
        `Display font “${t.display_font}” — use for headlines, titles, and large display text`,
      );
    }
    if (t.body_font) {
      lines.push(
        `Body font “${t.body_font}” — use for paragraphs, body copy, and supporting text`,
      );
    }
  }
  if (t.dos) lines.push(`Do’s: ${t.dos}`);
  if (t.donts) lines.push(`Don’ts: ${t.donts}`);
  if (t.preferred_words) lines.push(`Preferred words: ${t.preferred_words}`);
  if (t.avoided_words) lines.push(`Words to avoid: ${t.avoided_words}`);
  if (t.freeform_notes) lines.push(`Additional brand notes: ${t.freeform_notes}`);

  if (lines.length === 0) return '';
  return `BRAND GUIDELINES (follow strictly when writing):\n${lines.map((l) => `- ${l}`).join('\n')}`;
}

function normalizeHexColor(raw: string | null | undefined): string {
  const v = (raw || '').trim();
  if (!v) return '';
  return v.startsWith('#') ? v.toLowerCase() : `#${v.toLowerCase()}`;
}

/**
 * Force brand button (and header) colors onto template sections.
 * Fixes AI / defaults that still emit blue CTAs — especially hero buttons.
 */
export function applyBrandColorsToEmailTemplateSections(
  sections: any[] | null | undefined,
  colors: { buttonColor?: string | null; headerColor?: string | null; primaryColor?: string | null },
): any[] {
  if (!Array.isArray(sections)) return [];
  const button =
    normalizeHexColor(colors.buttonColor) ||
    normalizeHexColor(colors.primaryColor);
  const header = normalizeHexColor(colors.headerColor);

  return sections.map((section) => {
    if (!section || typeof section !== 'object') return section;
    const content = section.content && typeof section.content === 'object'
      ? { ...section.content }
      : section.content;

    if (section.type === 'button' && content && typeof content === 'object' && button) {
      content.backgroundColor = button;
      if (!content.color) content.color = '#ffffff';
    }

    if (section.type === 'hero' && content && typeof content === 'object' && button && content.buttonText) {
      content.buttonBackgroundColor = button;
      content.buttonColor = content.buttonColor || '#ffffff';
    }

    if (section.type === 'header' && content && typeof content === 'object' && header) {
      content.backgroundColor = header;
    }

    return { ...section, content };
  });
}

export async function getBrandGuidelines(
  businessId: string,
): Promise<BrandGuidelines | null> {
  if (!supabase || !businessId) return null;
  const { data, error } = await supabase
    .from('business_brand_guidelines')
    .select('*')
    .eq('business_id', businessId)
    .maybeSingle();

  if (error) {
    console.error('Failed to load brand guidelines:', error);
    throw error;
  }
  return (data as BrandGuidelines) || null;
}

export async function upsertBrandGuidelines(
  businessId: string,
  input: BrandGuidelinesInput,
): Promise<BrandGuidelines> {
  if (!supabase || !businessId) throw new Error('Missing business or Supabase client');
  const fields = trimFields(input);
  const payload = {
    business_id: businessId,
    ...fields,
    updated_at: new Date().toISOString(),
  };

  const { data, error } = await supabase
    .from('business_brand_guidelines')
    .upsert(payload, { onConflict: 'business_id' })
    .select('*')
    .maybeSingle();

  if (error) {
    console.error('Failed to save brand guidelines:', error);
    const msg = error.message || 'Unknown error';
    if (/website_url|online_booking_url|button_color|header_color/i.test(msg) || /column .* does not exist/i.test(msg)) {
      throw new Error(
        'Database is missing a brand guidelines column. Run the latest brand guidelines SQL in Supabase, then try again.',
      );
    }
    if (/permission|policy|rls/i.test(msg)) {
      throw new Error('You do not have permission to update brand guidelines for this business.');
    }
    throw new Error(msg);
  }

  return (data as BrandGuidelines) || ({ business_id: businessId, ...fields } as BrandGuidelines);
}

/**
 * Resolve the business's default public online booking URL (if a portal exists).
 */
export async function resolveDefaultOnlineBookingUrl(
  businessId: string,
  businessSlug: string | null | undefined,
): Promise<string> {
  if (!supabase || !businessId || !businessSlug) return '';
  try {
    const { data, error } = await supabase
      .from('online_booking_portals')
      .select('is_default, public_slug, custom_hostname, custom_host_verified_at')
      .eq('business_id', businessId)
      .order('is_default', { ascending: false })
      .limit(5);

    if (error || !data?.length) return '';

    const portal =
      data.find((p) => p.is_default) ||
      data.find((p) => !p.public_slug) ||
      data[0];

    const { buildPublicOnlineBookingUrl, getOnlineBookingPublicOriginFromPortalRow } =
      await import('./onlineBookingUrls');

    return buildPublicOnlineBookingUrl(
      getOnlineBookingPublicOriginFromPortalRow(portal),
      businessSlug,
      portal,
    );
  } catch (err) {
    console.warn('Failed to resolve default online booking URL:', err);
    return '';
  }
}
